import React from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import {isEqual, difference, keys, omit} from 'lodash';
import {autobind} from 'core-decorators';
import Portal from '@material-ui/core/Portal';
import fastSetStyle from './fastSetStyle';

export {
    createReactMapboxControl,
    MapboxControlGroup,
    MapboxControlButton,
    MapboxControlButtonProps
} from './MapboxControl';

export type MapboxImage = (
    HTMLImageElement |
    ImageData |
    {width: number; height: number; data: (Uint8Array | Uint8ClampedArray)}
);

export type MapboxControlInstance<Props> = mapboxgl.Control & { setProps?: (props: Props) => void };

export type MapboxControl<Props> = [
    {new (props: Props): MapboxControlInstance<Props>},
    Props,
    ('top-right' | 'top-left' | 'bottom-right' | 'bottom-left')?
];

export interface MapboxPopup extends mapboxgl.PopupOptions {
    children: React.ReactElement<any>;
    lngLat: mapboxgl.LngLatLike;
    closeOnClick?: false; // this option does not work well with React
    closeButton?: false; // this option does not work well with React
}

export interface MapboxProps {
    controls?: MapboxControl<any>[];
    images?: {[id: string]: MapboxImage};
    mapboxRef?: (map: mapboxgl.Map) => void;
    mapboxStyle: mapboxgl.Style;
    onClick?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onDblClick?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onError?: (event: mapboxgl.ErrorEvent) => void;
    onMouseMove?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onMouseDown?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onMouseUp?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onMouseOut?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onMove?: (event: mapboxgl.MapboxEvent) => void;
    onMoveEnd?: (event: mapboxgl.MapboxEvent) => void;
    onTouchEnd?: (event: mapboxgl.MapTouchEvent) => void;
    onTouchMove?: (event: mapboxgl.MapTouchEvent) => void;
    onTouchStart?: (event: mapboxgl.MapTouchEvent) => void;
    options: Partial<mapboxgl.MapboxOptions>;
    popup?: MapboxPopup;
    style?: React.CSSProperties;
    className?: string;
    transformRequest?: mapboxgl.TransformRequestFunction;
}

// This component aspires to expose an idiomatic React API for Mapbox GL without
// knowledge of any Gaia-specific things.
export default class Mapbox extends React.Component<MapboxProps> {
    containerRef: React.RefObject<HTMLDivElement> = React.createRef();
    controlInstances: MapboxControlInstance<any>[] = [];
    map: mapboxgl.Map | null = null;
    popupContainer = document.createElement('div');
    popupInstance?: mapboxgl.Popup;
    popupInstanceOptions?: mapboxgl.PopupOptions;

    static defaultProps = {
        controls: []
    };

    componentDidMount() {
        this.map = new mapboxgl.Map({
            ...this.props.options,
            container: this.containerRef.current!,
            style: this.props.mapboxStyle,
            transformRequest: (url: string, resourceType: mapboxgl.ResourceType) => {
                if (this.props.transformRequest) {
                    return this.props.transformRequest(url, resourceType);
                }
                return {url};
            },
        });


        if (this.props.mapboxRef) this.props.mapboxRef(this.map);

        this.updateEventListeners({}, this.props);
        this.updateMapboxStyle();
        this.updateControls([]);
        this.updateImages({});

        // Please don't use this for anything except debugging.
        (window as any).map = this.map;
    }

    componentWillUnmount() {
        this.updateEventListeners(this.props, {});
        if (this.map) this.map.remove();
    }

    updateEventListeners(prev: Partial<MapboxProps>, next: Partial<MapboxProps> = this.props) {
        this.updateEventListener('click', prev.onClick, next.onClick);
        this.updateEventListener('dblclick', prev.onDblClick, next.onDblClick);
        this.updateEventListener('error', prev.onError, next.onError);
        this.updateEventListener('mousedown', prev.onMouseDown, next.onMouseDown);
        this.updateEventListener('mousemove', prev.onMouseMove, next.onMouseMove);
        this.updateEventListener('mouseup', prev.onMouseUp, next.onMouseUp);
        this.updateEventListener('mouseout', prev.onMouseOut, next.onMouseOut);
        this.updateEventListener('move', prev.onMove, next.onMove);
        this.updateEventListener('moveend', prev.onMoveEnd, next.onMoveEnd);
        this.updateEventListener('touchend', prev.onTouchEnd, next.onTouchEnd);
        this.updateEventListener('touchmove', prev.onTouchMove, next.onTouchMove);
        this.updateEventListener('touchstart', prev.onTouchStart, next.onTouchStart);
    }

    updateEventListener(event: string, prev?: (event: any) => void, next?: (event: any) => void) {
        if (this.map && prev !== next) {
            if (prev) this.map.off(event, prev);
            if (next) this.map.on(event, next);
        }
    }

    componentDidUpdate(prevProps: MapboxProps) {
        this.updateEventListeners(prevProps);
        this.updateMapboxStyle(prevProps.mapboxStyle);
        this.updateControls(prevProps.controls);
        this.updateImages(prevProps.images);
        this.updatePopup();
    }


    @autobind
    updatePopup() {
        if (this.props.popup) {
            const {lngLat, ...options} = omit(this.props.popup, 'children');

            if (!this.popupInstance || !isEqual(this.popupInstanceOptions, options)) {
                if (this.popupInstance) this.popupInstance.remove();
                this.popupInstance = new mapboxgl.Popup({
                    ...options,
                    closeButton: false,
                    closeOnClick: false,
                    maxWidth: 'none',
                });
                this.popupInstance.setDOMContent(this.popupContainer);
                this.popupInstanceOptions = options;
            }
            this.popupInstance.setLngLat(lngLat);
            this.popupInstance.addTo(this.map!);

        } else if (this.popupInstance) {
            this.popupInstance.remove();
        }
    }

    updateMapboxStyle(prevStyle?: mapboxgl.Style) {
        if (!this.map) return;
        fastSetStyle(this.map, prevStyle, this.props.mapboxStyle);
    }

    updateImages(prevImages: {[id: string]: MapboxImage} = {}) {
        if (!this.map) return;
        if (!this.props.mapboxStyle) return;
        if (!this.props.mapboxStyle.sprite) return;

        const images = this.props.images || {};

        const prevImageIds = keys(prevImages).filter(id => this.map!.hasImage(id));
        const imageIds = keys(images);

        for (const id of difference(prevImageIds, imageIds)) {
            this.map.removeImage(id);
        }

        for (const id of difference(imageIds, prevImageIds)) {
            this.map.addImage(id, images[id], {pixelRatio: 2});
        }
    }

    updateControls(prevControls: MapboxControl<any>[] = []): void {
        if (!this.map) return;
        const {controls: controls = []} = this.props;

        if (isEqual(prevControls, controls)) return;

        // If the only thing that changed is the props of controls which
        // implement a "setProps" method, do an incremental update. Otherwise,
        // delete all controls and recreate them from scratch.

        if (prevControls.length !== controls.length) return this.descructivelyUpdateControls();
        for (let i = 0; i < controls.length; i++) {
            const control = controls[i];
            const prevControl = prevControls[i];
            const controlInstance = this.controlInstances[i];

            if (control[0] !== prevControl[0]) return this.descructivelyUpdateControls();
            else if (control[2] !== prevControl[2]) return this.descructivelyUpdateControls();
            else if (isEqual(control[1], prevControl[1])) continue;
            else if (controlInstance.setProps) controlInstance.setProps(control[1]);
            else return this.descructivelyUpdateControls();
        }
    }

    descructivelyUpdateControls() {
        if (!this.map) return;
        const {controls: controls = []} = this.props;

        for (const instance of this.controlInstances) {
            this.map.removeControl(instance);
        }

        this.controlInstances = [];

        for (const [constructor, options, position] of controls) {
            const instance = new constructor(options);
            this.map.addControl(instance, position);
            this.controlInstances.push(instance);
        }
    }

    render() {
        return <>
            <div
                ref={this.containerRef}
                style={this.props.style}
                className={this.props.className}
            />
            <Portal container={this.popupContainer}>
                {this.props.popup ? this.props.popup.children : <div />}
            </Portal>
        </>;
    }
}
